import React, { useEffect, useState } from 'react';
import { Spin, Typography } from 'antd';
import { IconMoodSad } from '@tabler/icons-react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../components/Layouts/Layout';
import LandingLayout from '../../components/Layouts/LandingLayout';
import BackButton from '../../components/BackButton';
import EmptyState from '../../components/EmptyState';
import HeavyDutyMachineryBookingWidget from '../../components/HeavyDutyMachineryBookingWidget';
import { useAuth } from '../../contextProviders/AuthProvider';
import { getHeavyDutyMachineryById } from '../../services/HeavyDutyMachineryService';
import { IHeavyDutyMachinery } from '../../interfaces/heavyDutyMachinery/IHeavyDutyMachinery';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';

const { Title } = Typography;

/**
 * Dedicated checkout page for a Machinery booking -- reached from the
 * "Reservar"/"Reservar ahora" CTAs on HeavyDutyMachineryDetail, which used
 * to scroll down to this same widget embedded at the bottom of that long
 * page. Pulled out onto its own route (rather than staying inline) so the
 * multi-step flow + its own sticky/bottom checkout summary
 * (HeavyDutyMachineryBookingWidget's own doc comment) get the whole screen
 * instead of competing with the gallery/specs/reviews above them.
 *
 * Public, no-auth, same convention as /heavy-duty-machinery/:id itself --
 * a guest can browse every step and see live prices before ever signing in;
 * identity is only required at the final "Confirmar y pagar" (see the
 * widget's own handleSubmit, which redirects to /login with
 * redirectAfterLogin set to THIS page so the guest lands right back here,
 * selections intact, after logging in).
 */
const HeavyDutyMachineryBookingPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [space, setSpace] = useState<IHeavyDutyMachinery | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    getHeavyDutyMachineryById(id)
      .then(setSpace)
      .finally(() => setLoading(false));
  }, [id]);

  // Same anonymous-vs-signed-in chrome switch as HeavyDutyMachineryDetail's
  // own withLayout -- see that component's doc comment for why. Mobile drops
  // MobileBottomNav here (hideBottomNav) since the widget's own fixed
  // bottom checkout bar already owns that screen edge on this page.
  const withLayout = (node: React.ReactNode) => {
    if (authLoading) return null;
    if (!isAuthenticated) return <LandingLayout>{node}</LandingLayout>;
    return <AppLayout hideBottomNav>{node}</AppLayout>;
  };

  if (loading) {
    return withLayout(
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!space) {
    return withLayout(
      <EmptyState
        icon={<IconMoodSad size={24} />}
        title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_NOT_FOUND)}
        description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_NOT_FOUND_DESCRIPTION)}
      />
    );
  }

  return withLayout(
    <div style={{ width: '100%', maxWidth: 960, textAlign: 'left' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <BackButton fallbackPath={`/heavy-duty-machinery/${space.id}`} />
        <Title level={4} style={{ margin: 0 }}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_SECTION_TITLE)}
        </Title>
      </div>
      <HeavyDutyMachineryBookingWidget space={space} />
    </div>
  );
};

export default HeavyDutyMachineryBookingPage;
